import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { forwardRef, useEffect, useState } from 'react';

import { TransferInLockedRecipient } from '../TransferInLockedRecipient/TransferInLockedRecipient';
import { ErrorBoundary } from '@wetransfer/transfer/component/ErrorBoundary/ErrorBoundary';
import Poptip from '@wetransfer/transfer/component/info/poptip';
import Scrollable from '@wetransfer/transfer/component/scrollable';
import Fields from '@wetransfer/transfer/component/transfer/uploader/fields';
import Files from '@wetransfer/transfer/component/transfer/uploader/files';
import LockedRecipient from '@wetransfer/transfer/component/transfer/uploader/locked-recipient';
import Options from '@wetransfer/transfer/component/transfer/uploader/options';
import {
  PRIMARY_BLUE_ACTIVE,
  PRIMARY_BLUE,
} from '@wetransfer/transfer/constant/colors';
import FFTransferInNameLogo from '@wetransfer/transfer/constant/flags/transfer-in-name-logo';
import { useCurrentTeam } from '@wetransfer/transfer/hooks/useCurrentTeam/useCurrentTeam';
import I18n from '@wetransfer/transfer/modules/i18n';

import {
  TRANSFER_OPTIONS_BUTTON_LABEL,
  TRANSFER_OPTIONS_BUTTON_TEST_ID,
} from './constants';
import { CustomWallpaperPopover } from './CustomWallpaperPopover';
import { useCustomWallpaperPopover } from './CustomWallpaperPopover/hooks';
import OptionsCustomWallpaperUpsellPopover from './OptionsCustomWallpaperUpsellPopover';
import { useOptionsCustomWallpaperUpsellPopover } from './OptionsCustomWallpaperUpsellPopover/hooks';
import { TransferOptionsButton } from './TransferOptionsButton';

const UploaderForm = forwardRef(
  (
    {
      channel,
      transfer,
      isCookieOverlayActive,
      isDisabled,
      isForwardedTransfer,
      isOptionsVisible,
      isRecipientLocked,
      isTooltip,
      changeTransferType,
      optionsRef,
      scrollableRef,
      hideOptionsOnScroll,
      hideOptions,
      handleLockedRecipientClick,
      toggleOptions,
      startTransfer,
      transferWindowRef,
    },
    ref
  ) => {
    const [
      timeoutIDForCustomWallpaperTooltip,
      setTimeoutIDForCustomWallpaperTooltip,
    ] = useState(null);

    const optionsColor = isOptionsVisible ? PRIMARY_BLUE_ACTIVE : PRIMARY_BLUE;
    const { currentTeam } = useCurrentTeam();

    const customWallpaperUpsellPopover =
      useOptionsCustomWallpaperUpsellPopover();

    const {
      // Note: this popover may be overridden by `isTooltip`, so it's not guaranteed it will show if this is `true`.
      isShowPopover: isShowCustomWallpaperPopover,
      onAcceptPopover,
      onDismissPopover,
      onViewPopover,
    } = useCustomWallpaperPopover();

    const transferOptionsLinkTooltip = (
      <>
        <p>{I18n.t('transfer.bubble.tips.prefer_link.text')}</p>
        <button
          className="popover__button"
          target="_self"
          onClick={() => changeTransferType('link')}
        >
          {I18n.t('transfer.bubble.tips.prefer_link.button')}
        </button>
      </>
    );

    const onInteractOutsideHandler = () => {
      const setTimeoutID = setTimeout(
        () => customWallpaperUpsellPopover.onDismiss(),
        5000
      );

      setTimeoutIDForCustomWallpaperTooltip(setTimeoutID);
    };

    /*this ensures that if the parent component is unmounted when the set timeout is ran
      we don't get an error and is cleaned away nicely (and only ran on mount)
    */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => () => clearTimeout(timeoutIDForCustomWallpaperTooltip), []);

    return (
      <div
        ref={ref}
        className={classNames(
          'transfer__window',
          'uploader',
          'uploader--form',
          `uploader--type-${transfer.type}`
        )}
        data-testid="uploaderForm"
      >
        <Scrollable
          ref={scrollableRef}
          className="transfer__contents"
          onScroll={hideOptionsOnScroll}
        >
          <Files
            panel={ref.current?.node}
            panelComponent={scrollableRef.current}
            onClick={hideOptions}
          />
          {isRecipientLocked &&
            (FFTransferInNameLogo.isOn() ? (
              // TODO: Pass the correct channel name after it starts being served by the backend
              <TransferInLockedRecipient channelName={channel.name} />
            ) : (
              <LockedRecipient
                recipient={channel.defaultRecipient}
                onClick={handleLockedRecipientClick}
              />
            ))}
          <Fields
            onInteraction={hideOptions}
            panel={scrollableRef.current}
            isRecipientLocked={isRecipientLocked}
            transferWindowRef={transferWindowRef}
            currentTeam={currentTeam}
          />
          <Options ref={optionsRef} isRecipientLocked={isRecipientLocked} />
        </Scrollable>
        <ErrorBoundary
          componentName="transfer-footer"
          logMessage="EMERGENCY: There was an exception rendering the footer"
        >
          <div className="transfer__footer">
            {isTooltip ? (
              <Poptip
                className="email-verification__transfer-options-tooltip"
                visible={true}
                content={transferOptionsLinkTooltip}
                anchor="bottom"
                offset={15}
                trigger="manual"
                setAutoHideTimer={true}
              >
                <TransferOptionsButton
                  onClick={toggleOptions}
                  color={optionsColor}
                  disabled={isCookieOverlayActive}
                  aria-label={TRANSFER_OPTIONS_BUTTON_LABEL}
                  data-testid={TRANSFER_OPTIONS_BUTTON_TEST_ID}
                />
              </Poptip>
            ) : customWallpaperUpsellPopover.isVisible ? (
              <OptionsCustomWallpaperUpsellPopover
                onAcceptPopover={customWallpaperUpsellPopover.onAccept}
                onDismissPopover={customWallpaperUpsellPopover.onDismiss}
                onViewPopover={onViewPopover}
                onInteractOutside={onInteractOutsideHandler}
                target={
                  <TransferOptionsButton
                    onClick={toggleOptions}
                    color={optionsColor}
                    disabled={isCookieOverlayActive}
                    aria-label={TRANSFER_OPTIONS_BUTTON_LABEL}
                    data-testid={TRANSFER_OPTIONS_BUTTON_TEST_ID}
                  />
                }
              />
            ) : isShowCustomWallpaperPopover ? (
              <CustomWallpaperPopover
                onAcceptPopover={onAcceptPopover}
                onDismissPopover={onDismissPopover}
                onViewPopover={onViewPopover}
                target={
                  <TransferOptionsButton
                    onClick={toggleOptions}
                    color={optionsColor}
                    disabled={isCookieOverlayActive}
                    aria-label={TRANSFER_OPTIONS_BUTTON_LABEL}
                    data-testid={TRANSFER_OPTIONS_BUTTON_TEST_ID}
                  />
                }
              />
            ) : (
              <TransferOptionsButton
                onClick={toggleOptions}
                color={optionsColor}
                disabled={isCookieOverlayActive}
                aria-label={TRANSFER_OPTIONS_BUTTON_LABEL}
                data-testid={TRANSFER_OPTIONS_BUTTON_TEST_ID}
              />
            )}
            <button
              type="button"
              disabled={isDisabled}
              onClick={startTransfer}
              className={classNames('transfer__button', {
                ['transfer__button--inactive']: isDisabled,
                ['transfer-link__url']: transfer.type === 'link',
              })}
              data-testid="uploaderForm-transfer-button"
            >
              {isForwardedTransfer && I18n.t('transfer.buttons.forward')}
              {!isForwardedTransfer &&
                transfer.type === 'email' &&
                I18n.t('transfer.buttons.transfer')}
              {!isForwardedTransfer &&
                transfer.type === 'link' &&
                I18n.t('transfer.buttons.get_a_link')}
            </button>
          </div>
        </ErrorBoundary>
      </div>
    );
  }
);

UploaderForm.displayName = 'UploaderForm';

UploaderForm.propTypes = {
  channel: PropTypes.object.isRequired,
  transfer: PropTypes.object.isRequired,
  isCookieOverlayActive: PropTypes.bool.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  isForwardedTransfer: PropTypes.bool.isRequired,
  isOptionsVisible: PropTypes.bool.isRequired,
  isRecipientLocked: PropTypes.bool.isRequired,
  isTooltip: PropTypes.bool,
  changeTransferType: PropTypes.func.isRequired,
  optionsRef: PropTypes.object,
  scrollableRef: PropTypes.object,
  hideOptionsOnScroll: PropTypes.func.isRequired,
  hideOptions: PropTypes.func.isRequired,
  handleLockedRecipientClick: PropTypes.func.isRequired,
  toggleOptions: PropTypes.func.isRequired,
  transferWindowRef: PropTypes.object,
  startTransfer: PropTypes.func.isRequired,
};

export default UploaderForm;
